/**
 * Profile — XP window with Account + Appearance tabs.
 *
 * Account:
 *   - Display name (editable)
 *   - Email (editable)
 *   - Avatar picker: file input → client-side canvas resize to ≤ 256px →
 *     POST /api/profile/avatar (multipart). "Remove" clears it.
 *
 * Appearance:
 *   - Wallpaper picker (curated presets + URL input)
 *   - Accent color swatches (writes settings.accent_color)
 *
 * All state lives in AuthContext so other components (Container background,
 * FooterMenu header) react immediately.
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useAuth } from 'auth/AuthContext';
import { ApiError } from 'api/client';

import defaultAvatar from 'assets/windowsIcons/user.png';
import defaultWallpaper from 'assets/desktop-bg.webp';

const TAB_ACCOUNT = 'account';
const TAB_APPEARANCE = 'appearance';

// Curated wallpaper presets. Value === settings.wallpaper_url. null means
// "use the bundled default". thumb is what shows in the picker grid.
const WALLPAPER_PRESETS = [
  {
    key: 'default',
    label: 'Bliss (default)',
    value: null,
    thumb: defaultWallpaper,
  },
];

const ACCENT_SWATCHES = [
  { key: 'blue', label: 'Blue', value: null }, // null = XP default blue
  { key: 'olive', label: 'Olive Green', value: '#6b8e23' },
  { key: 'silver', label: 'Silver', value: '#8896a8' },
  { key: 'royal', label: 'Royal', value: '#4d47c8' },
  { key: 'sunset', label: 'Sunset', value: '#e76f24' },
  { key: 'rose', label: 'Rose', value: '#c8478e' },
];

const AVATAR_MAX_DIM = 256;
const AVATAR_MAX_BYTES = 512 * 1024;

function Profile({ onClose }) {
  const {
    user,
    settings,
    updateProfile,
    updateSettings,
    uploadAvatar,
    deleteAvatar,
    resendVerification,
  } = useAuth();

  const [tab, setTab] = useState(TAB_ACCOUNT);
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | error

  // Local form state (mirrors user until Save is pressed).
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null); // dataURL for pending upload
  const [avatarBlob, setAvatarBlob] = useState(null); // pending upload blob
  const [wallpaperUrl, setWallpaperUrl] = useState(null);
  const [wallpaperInput, setWallpaperInput] = useState('');
  const [accentColor, setAccentColor] = useState(null);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  // Hydrate from context when user/settings changes.
  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  useEffect(() => {
    setWallpaperUrl(settings.wallpaper_url || null);
    setAccentColor(settings.accent_color || null);
    // wallpaperInput is only for the "custom URL" text box — leave it alone
    // when the value already matches a preset.
    if (
      settings.wallpaper_url &&
      !WALLPAPER_PRESETS.some(p => p.value === settings.wallpaper_url)
    ) {
      setWallpaperInput(settings.wallpaper_url);
    }
  }, [settings]);

  function onPickFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      setError('Please choose a PNG, JPEG, or WebP image.');
      return;
    }
    setError(null);
    resizeToBlob(file, AVATAR_MAX_DIM)
      .then(({ blob, dataUrl }) => {
        if (blob.size > AVATAR_MAX_BYTES) {
          setError('That image is still larger than 512 KB after resize.');
          return;
        }
        setAvatarBlob(blob);
        setAvatarPreview(dataUrl);
      })
      .catch(err => {
        setError(err.message || 'Could not read that image.');
      });
  }

  async function onSaveAccount(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const patch = {};
      if (
        displayName.trim() &&
        displayName.trim() !== (user.display_name || '')
      ) {
        patch.display_name = displayName.trim();
      }
      if (email.trim() && email.trim() !== (user.email || '')) {
        patch.email = email.trim();
      }
      if (Object.keys(patch).length) {
        await updateProfile(patch);
      }
      if (avatarBlob) {
        await uploadAvatar(avatarBlob);
        setAvatarBlob(null);
        setAvatarPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      setMessage('Saved.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function onResendVerification() {
    if (resendState === 'sending') return;
    setResendState('sending');
    try {
      await resendVerification();
      setResendState('sent');
    } catch (err) {
      setResendState('error');
    }
  }

  async function onRemoveAvatar() {
    if (busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await deleteAvatar();
      setAvatarBlob(null);
      setAvatarPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setMessage('Avatar removed.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSaveAppearance(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await updateSettings({
        wallpaper_url: wallpaperUrl,
        accent_color: accentColor,
      });
      setMessage('Saved.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const avatarSrc =
    avatarPreview ||
    (user && user.avatar_path ? '/api/' + user.avatar_path : defaultAvatar);

  if (!user) {
    return (
      <Div>
        <div className="profile__empty">
          <p>You need to be signed in to view your profile.</p>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </Div>
    );
  }

  return (
    <Div>
      <div className="profile__tabs">
        <button
          type="button"
          className={
            'profile__tab' +
            (tab === TAB_ACCOUNT ? ' profile__tab--active' : '')
          }
          onClick={() => {
            setTab(TAB_ACCOUNT);
            setError(null);
            setMessage(null);
          }}
        >
          Account
        </button>
        <button
          type="button"
          className={
            'profile__tab' +
            (tab === TAB_APPEARANCE ? ' profile__tab--active' : '')
          }
          onClick={() => {
            setTab(TAB_APPEARANCE);
            setError(null);
            setMessage(null);
          }}
        >
          Appearance
        </button>
      </div>

      <section className="profile__body">
        {tab === TAB_ACCOUNT ? (
          <form onSubmit={onSaveAccount}>
            <div className="profile__avatar-row">
              <img
                src={avatarSrc}
                alt="avatar"
                className="profile__avatar-preview"
              />
              <div className="profile__avatar-controls">
                <button
                  type="button"
                  onClick={() =>
                    fileInputRef.current && fileInputRef.current.click()
                  }
                  disabled={busy}
                >
                  Change picture…
                </button>
                {user.avatar_path && (
                  <button
                    type="button"
                    onClick={onRemoveAvatar}
                    disabled={busy}
                  >
                    Remove
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  style={{ display: 'none' }}
                  onChange={onPickFile}
                />
                <div className="profile__hint">
                  PNG, JPEG, or WebP. Resized to 256px on your device before
                  upload.
                </div>
              </div>
            </div>

            <Field label="User name">
              <input type="text" value={user.username || ''} disabled />
            </Field>
            <Field label="Display name">
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                maxLength={64}
                disabled={busy}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={busy}
              />
            </Field>
            {user && user.email_verified === false && (
              <div className="profile__verify-banner">
                <span>Your email address is not verified.</span>
                {resendState === 'sending' ? (
                  <span>Sending…</span>
                ) : resendState === 'sent' ? (
                  <span>Check your inbox.</span>
                ) : (
                  <button
                    type="button"
                    className="profile__link"
                    onClick={onResendVerification}
                  >
                    Resend verification email
                  </button>
                )}
              </div>
            )}

            {error && <div className="profile__error">{error}</div>}
            {message && <div className="profile__message">{message}</div>}

            <div className="profile__actions">
              <button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={onClose} disabled={busy}>
                Close
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={onSaveAppearance}>
            <div className="profile__section-title">Desktop background</div>
            <div className="profile__wallpaper-grid">
              {WALLPAPER_PRESETS.map(preset => (
                <button
                  key={preset.key}
                  type="button"
                  className={
                    'profile__wallpaper' +
                    (wallpaperUrl === preset.value
                      ? ' profile__wallpaper--active'
                      : '')
                  }
                  onClick={() => {
                    setWallpaperUrl(preset.value);
                    setWallpaperInput('');
                  }}
                >
                  <img src={preset.thumb} alt={preset.label} />
                  <span>{preset.label}</span>
                </button>
              ))}
            </div>
            <Field label="Or paste an image URL">
              <input
                type="url"
                placeholder="https://…"
                value={wallpaperInput}
                onChange={e => {
                  setWallpaperInput(e.target.value);
                  setWallpaperUrl(e.target.value.trim() || null);
                }}
                maxLength={300}
                disabled={busy}
              />
            </Field>

            <div className="profile__section-title">Accent color</div>
            <div className="profile__swatches">
              {ACCENT_SWATCHES.map(sw => (
                <button
                  key={sw.key}
                  type="button"
                  title={sw.label}
                  className={
                    'profile__swatch' +
                    (accentColor === sw.value ? ' profile__swatch--active' : '')
                  }
                  style={{ background: sw.value || '#245edc' }}
                  onClick={() => setAccentColor(sw.value)}
                />
              ))}
            </div>

            {error && <div className="profile__error">{error}</div>}
            {message && <div className="profile__message">{message}</div>}

            <div className="profile__actions">
              <button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={onClose} disabled={busy}>
                Close
              </button>
            </div>
          </form>
        )}
      </section>
    </Div>
  );
}

function Field({ label, children }) {
  return (
    <label className="profile__field">
      <span className="profile__field__label">{label}</span>
      {children}
    </label>
  );
}

/**
 * Read `file`, draw it into an off-screen canvas scaled so the longest edge
 * is ≤ `maxDim`, and return a JPEG Blob + preview data URL. Preserves alpha
 * as PNG when the source is PNG or WebP.
 */
function resizeToBlob(file, maxDim) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.onload = () => {
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const outType =
          file.type === 'image/png' || file.type === 'image/webp'
            ? 'image/png'
            : 'image/jpeg';
        const quality = outType === 'image/jpeg' ? 0.9 : undefined;
        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Could not encode image'));
              return;
            }
            resolve({ blob, dataUrl: canvas.toDataURL(outType, quality) });
          },
          outType,
          quality,
        );
      };
      img.onerror = () => reject(new Error('That file is not a valid image'));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function errorMessage(err) {
  if (err instanceof ApiError) {
    const map = {
      invalid_email: 'That email address does not look valid.',
      email_taken: 'That email is already registered.',
      invalid_display_name: 'Display name must be 1-64 characters.',
      invalid_image: 'Only PNG, JPEG, or WebP images are allowed.',
      file_too_large: 'That image is too large (max 512 KB).',
      csrf_invalid: 'Session expired — please reload the page.',
      unauthorized: 'Please sign in again.',
      network_error: 'Cannot reach the server. Check your connection.',
    };
    return map[err.code] || err.message || 'Something went wrong.';
  }
  return (err && err.message) || 'Something went wrong.';
}

const Div = styled.div`
  background: #ece9d8;
  color: #000;
  font-family: Tahoma, sans-serif;
  font-size: 11px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 12px 12px;
  box-sizing: border-box;

  .profile__tabs {
    display: flex;
    gap: 2px;
    margin-bottom: -1px;
  }
  .profile__tab {
    background: #ece9d8;
    border: 1px solid #a4b0be;
    border-bottom-color: #ece9d8;
    padding: 4px 12px;
    font: inherit;
    cursor: pointer;
  }
  .profile__tab--active {
    background: #fff;
    font-weight: bold;
    position: relative;
    z-index: 1;
  }

  .profile__body {
    background: #fff;
    border: 1px solid #a4b0be;
    padding: 12px;
    flex: 1;
    overflow-y: auto;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .profile__section-title {
    font-weight: bold;
    color: #0b3d91;
    margin-top: 6px;
  }
  .profile__section-title:first-child {
    margin-top: 0;
  }

  .profile__field {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .profile__field__label {
    font-weight: bold;
  }
  input[type='text'],
  input[type='email'],
  input[type='url'] {
    font: inherit;
    padding: 3px 4px;
    border: 1px solid #7f9db9;
    background: #fff;
  }
  input[disabled] {
    background: #f4f4f4;
    color: #666;
  }

  .profile__avatar-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
  .profile__avatar-preview {
    width: 72px;
    height: 72px;
    border-radius: 4px;
    border: 2px solid #a4b0be;
    object-fit: cover;
    background: #fff;
  }
  .profile__avatar-controls {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .profile__avatar-controls button {
    align-self: flex-start;
  }
  .profile__hint {
    color: #555;
    font-size: 10px;
  }

  .profile__wallpaper-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .profile__wallpaper {
    background: none;
    border: 2px solid transparent;
    padding: 2px;
    cursor: pointer;
    font: inherit;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
  }
  .profile__wallpaper img {
    width: 84px;
    height: 60px;
    object-fit: cover;
    border: 1px solid #a4b0be;
  }
  .profile__wallpaper--active {
    border-color: #245edc;
  }

  .profile__swatches {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .profile__swatch {
    width: 26px;
    height: 26px;
    border-radius: 4px;
    border: 2px solid #a4b0be;
    cursor: pointer;
  }
  .profile__swatch--active {
    border-color: #000;
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px #245edc;
  }

  .profile__error {
    background: #fdecec;
    border: 1px solid #d13438;
    color: #8b0000;
    padding: 4px 6px;
  }
  .profile__message {
    background: #e6f4ea;
    border: 1px solid #4caf50;
    color: #206b2c;
    padding: 4px 6px;
  }

  .profile__verify-banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    background: #fff8e1;
    border: 1px solid #d9b45a;
    color: #6b4b00;
    padding: 4px 6px;
    margin-top: -4px;
    margin-bottom: 6px;
  }
  .profile__link {
    background: none;
    border: none;
    color: #0032c9;
    text-decoration: underline;
    padding: 0;
    font: inherit;
    cursor: pointer;
  }

  .profile__actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
    margin-top: 6px;
  }
  .profile__actions button,
  .profile__avatar-controls button {
    min-width: 72px;
    padding: 3px 10px;
    font: inherit;
    background: linear-gradient(#fefefe 0%, #dedede 100%);
    border: 1px solid #003c74;
    border-radius: 3px;
    cursor: pointer;
  }
  .profile__actions button:active,
  .profile__avatar-controls button:active {
    background: linear-gradient(#dedede 0%, #fefefe 100%);
  }
  .profile__actions button[disabled],
  .profile__avatar-controls button[disabled] {
    color: #888;
    cursor: default;
  }

  .profile__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 8px;
  }
`;

export default Profile;
